import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { LuLoaderCircle, LuUpload, LuFileDown } from 'react-icons/lu';
import { collection, writeBatch, doc } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { db } from '../../config/firebaseConfig';
import { splitDuplicateRows } from './budgetImportDedupe';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import useBudgetText from './budgetText';
import ImportPanel from './parts/ImportPanel';

const EXPECTED_COLUMNS = [
  'GİB NUMARASI', 'TASLAK NO', 'SENARYO', 'FATURA TİPİ',
  'OLUŞTURMA TARİHİ', 'OLUŞTURMA ZAMANI', 'BELGE TARİHİ', 'BELGE ZAMANI',
  'MÜŞTERİ TCKN/VKN', 'UNVAN', 'MÜŞTERİ ETİKETİ',
  'TUTAR', 'PARA BİRİMİ', 'ZARF UUID', 'ZARF DURUMU', 'ETTN',
  'İSKONTO', 'FATURA DIŞI MASRAFLARI', 'SATIR SAYISI',
  'BİRİM FİYAT * MİKTAR TUTARI', 'ÖDENECEK TUTAR',
  'VERGİLER HARİÇ TUTAR', 'VERGİLER DAHİL TUTAR'
];

// Some e-Fatura portals export "Excel" files that are actually HTML tables.
// SheetJS parses them but leaves HTML entities (e.g. &#231; for ç) in cell values.
const decodeHtmlEntities = (val) => {
  if (typeof val !== 'string') return val;
  if (val.indexOf('&') === -1) return val;
  // SheetJS' HTML parser can double-encode (e.g. &amp;#231;), so loop until stable.
  let out = val;
  for (let i = 0; i < 4; i++) {
    const next = out
      .replace(/&amp;/gi, '&')
      .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCodePoint(parseInt(hex, 16)))
      .replace(/&#(\d+);/g, (_, dec) => String.fromCodePoint(parseInt(dec, 10)))
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&apos;/gi, "'")
      .replace(/&nbsp;/gi, ' ');
    if (next === out) break;
    out = next;
  }
  return out;
};

// Parse Turkish-formatted amount like "506.000,00" -> 506000
const parseAmount = (val) => {
  if (val === null || val === undefined || val === '') return 0;
  if (typeof val === 'number') return val;
  const s = String(decodeHtmlEntities(val)).trim().replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
};

// Normalize time -> "HH:MM:SS"
const parseTime = (val) => {
  if (val === null || val === undefined || val === '') return '';
  if (val instanceof Date && !isNaN(val)) {
    const h = String(val.getHours()).padStart(2, '0');
    const m = String(val.getMinutes()).padStart(2, '0');
    const s = String(val.getSeconds()).padStart(2, '0');
    return `${h}:${m}:${s}`;
  }
  if (typeof val === 'number' && val >= 0 && val < 1) {
    const total = Math.round(val * 86400);
    const h = String(Math.floor(total / 3600) % 24).padStart(2, '0');
    const m = String(Math.floor(total / 60) % 60).padStart(2, '0');
    const s = String(total % 60).padStart(2, '0');
    return `${h}:${m}:${s}`;
  }
  return String(val).trim();
};

// Normalize date from Excel (Date, number serial, or "DD.MM.YYYY") -> "YYYY-MM-DD"
const parseDate = (val) => {
  if (!val && val !== 0) return '';
  if (val instanceof Date && !isNaN(val)) {
    const y = val.getFullYear();
    const m = String(val.getMonth() + 1).padStart(2, '0');
    const d = String(val.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  if (typeof val === 'number') {
    const parsed = XLSX.SSF.parse_date_code(val);
    if (parsed) {
      const y = parsed.y;
      const m = String(parsed.m).padStart(2, '0');
      const d = String(parsed.d).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  }
  const s = String(val).trim();
  // DD.MM.YYYY or D.M.YYYY
  const m1 = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (m1) {
    return `${m1[3]}-${m1[2].padStart(2, '0')}-${m1[1].padStart(2, '0')}`;
  }
  // YYYY-MM-DD pass-through
  const m2 = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m2) return s;
  return '';
};

/**
 * "Excel'den Gelir İçe Aktar" (Tailwind). Parsing, duplicate checks and the
 * batched writes are the old code; only the screen and the texts changed.
 */
const ImportIncomeExcelModal = ({ isOpen, onClose, onImported }) => {
  const { t, language, label } = useBudgetText();
  // Toasts and messages read the current language through a ref
  const labelRef = useRef(label);
  labelRef.current = label;
  const msg = (key, vars) => labelRef.current(`toast.${key}`, vars);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [isChecking, setIsChecking] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState({ existing: 0, inFile: 0 });
  const fileInputRef = useRef(null);

  const resetState = () => {
    setFile(null);
    setRows([]);
    setParseError('');
    setProgress({ done: 0, total: 0 });
    setDuplicateInfo({ existing: 0, inFile: 0 });
  };

  useEffect(() => {
    if (!isOpen) resetState();
  }, [isOpen]);

  const readWorkbook = async (f) => {
    const buf = await f.arrayBuffer();
    const bytes = new Uint8Array(buf);
    // xlsx/zip files start with "PK"
    const isZip = bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4B;
    if (isZip) {
      return XLSX.read(buf, { type: 'array', cellDates: true });
    }
    // text (csv/tsv) — detect BOM and decode ourselves so XLSX doesn't call its
    // unbundled codepage table for UTF-16 content
    let encoding = 'utf-8';
    if (bytes.length >= 2 && bytes[0] === 0xFF && bytes[1] === 0xFE) encoding = 'utf-16le';
    else if (bytes.length >= 2 && bytes[0] === 0xFE && bytes[1] === 0xFF) encoding = 'utf-16be';
    const text = new TextDecoder(encoding).decode(buf).replace(/^﻿/, '');
    return XLSX.read(text, { type: 'string', cellDates: true });
  };

  const parseFile = async (f) => {
    setParseError('');
    setDuplicateInfo({ existing: 0, inFile: 0 });
    setIsChecking(true);
    try {
      const wb = await readWorkbook(f);
      const sheetName = wb.SheetNames[0];
      const sheet = wb.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: true });

      if (!json.length) {
        setParseError(labelRef.current('import.errorEmpty'));
        setRows([]);
        return;
      }

      // Validate that at least TUTAR and UNVAN exist
      const firstKeys = Object.keys(json[0]).map(k => k.trim());
      const hasRequired = firstKeys.some(k => k === 'TUTAR') &&
                          firstKeys.some(k => k === 'UNVAN');
      if (!hasRequired) {
        setParseError(labelRef.current('import.errorFormatIncome'));
        setRows([]);
        return;
      }

      const mapped = json.map((r) => {
        const get = (key) => {
          if (r[key] !== undefined) return r[key];
          // fallback case-insensitive / trimmed match
          const match = Object.keys(r).find(k => k.trim() === key);
          return match ? r[match] : '';
        };
        const str = (key) => decodeHtmlEntities(String(get(key) ?? '')).trim();
        const musteri = str('UNVAN');
        const gib = str('GİB NUMARASI');
        const tutar = parseAmount(get('TUTAR'));
        const paraBirimi = str('PARA BİRİMİ') || 'TRY';
        const belgeTarihi = parseDate(get('BELGE TARİHİ')) || parseDate(get('OLUŞTURMA TARİHİ'));
        return {
          BütçeAdı: gib ? `${musteri} - ${gib}` : musteri || 'E-Fatura',
          BütçeKaynak: musteri,
          BütçeSahibi: 'Şirket',
          BütçeTipi: 'Gelir',
          GiderTipi: 'Gelir',
          BütçeKategorisi: 'E-Fatura',
          ParaBirimi: paraBirimi,
          KDV: '0',
          BütçeMiktarı: tutar,
          BütçeTarihi: belgeTarihi,
          BeklenenÖdemeTarihi: belgeTarihi,
          ÖdemeDurumu: 'Alındı',
          AktifDurum: 'Aktif',
          GibNumarasi: gib,
          TaslakNo: str('TASLAK NO'),
          Senaryo: str('SENARYO'),
          FaturaTipi: str('FATURA TİPİ'),
          OlusturmaTarihi: parseDate(get('OLUŞTURMA TARİHİ')),
          OlusturmaZamani: parseTime(get('OLUŞTURMA ZAMANI')),
          BelgeTarihi: parseDate(get('BELGE TARİHİ')),
          BelgeZamani: parseTime(get('BELGE ZAMANI')),
          MusteriTcknVkn: str('MÜŞTERİ TCKN/VKN'),
          MusteriUnvan: musteri,
          MusteriEtiketi: str('MÜŞTERİ ETİKETİ'),
          Tutar: tutar,
          ZarfUuid: str('ZARF UUID'),
          ZarfDurumu: str('ZARF DURUMU'),
          Ettn: str('ETTN'),
          Iskonto: parseAmount(get('İSKONTO')),
          FaturaDisiMasraflari: parseAmount(get('FATURA DIŞI MASRAFLARI')),
          SatirSayisi: parseAmount(get('SATIR SAYISI')),
          BirimFiyatMiktarTutari: parseAmount(get('BİRİM FİYAT * MİKTAR TUTARI')),
          OdenecekTutar: parseAmount(get('ÖDENECEK TUTAR')),
          VergilerHaricTutar: parseAmount(get('VERGİLER HARİÇ TUTAR')),
          VergilerDahilTutar: parseAmount(get('VERGİLER DAHİL TUTAR')),
        };
      });
      // Skip rows that already exist in Firestore as an exact match (all fields identical),
      // and rows that repeat inside the same file.
      const { newRows, existingDuplicates, inFileDuplicates } = await splitDuplicateRows(mapped);
      setDuplicateInfo({ existing: existingDuplicates, inFile: inFileDuplicates });
      setRows(newRows);
    } catch (err) {
      console.error(err);
      setParseError(labelRef.current('import.errorRead'));
      setRows([]);
    } finally {
      setIsChecking(false);
    }
  };

  const handleFile = (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      toast.error(msg('invalidExcel'));
      return;
    }
    setFile(f);
    parseFile(f);
  };

  const handleFileInputChange = (e) => {
    handleFile(e.target.files?.[0]);
    e.target.value = '';
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handleImport = async () => {
    if (!rows.length) return;
    setIsImporting(true);
    setProgress({ done: 0, total: rows.length });
    try {
      // Re-check right before writing so records added since the file was parsed are not duplicated either.
      const { newRows: toWrite, existingDuplicates } = await splitDuplicateRows(rows);
      const skipped = duplicateInfo.existing + existingDuplicates;
      if (!toWrite.length) {
        toast.info(msg('allExist'));
        onClose();
        return;
      }
      setProgress({ done: 0, total: toWrite.length });
      // Firestore limit: 500 ops per batch
      const chunkSize = 400;
      const createdAt = new Date().toISOString();
      let done = 0;
      for (let i = 0; i < toWrite.length; i += chunkSize) {
        const chunk = toWrite.slice(i, i + chunkSize);
        const batch = writeBatch(db);
        chunk.forEach((row) => {
          const ref = doc(collection(db, 'Budgets'));
          batch.set(ref, { ...row, createdAt, importSource: 'excel-gelir' });
        });
        await batch.commit();
        done += chunk.length;
        setProgress({ done, total: toWrite.length });
      }
      toast.success(`${msg('incomeImported', { count: toWrite.length })}${skipped ? ` ${msg('skippedRows', { count: skipped })}` : ''}`);
      onImported && onImported();
      onClose();
    } catch (err) {
      console.error('Import error:', err);
      toast.error(msg('importError'));
    } finally {
      setIsImporting(false);
    }
  };

  const totalAmount = rows.reduce((s, r) => s + (parseFloat(r.BütçeMiktarı) || 0), 0);

  let importLabel = label('import.import');
  if (isImporting) importLabel = label('import.importing');
  else if (rows.length) importLabel = label('import.importCount', { count: rows.length });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      // Not closed by accident while the rows are being written
      dismissible={!isImporting}
      size="lg"
      title={label('import.titleIncome')}
      subtitle={label('import.subtitleIncome')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-teal-400 tw-to-emerald-600 tw-shadow-emerald-500/30" size="xs">
          <LuFileDown aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={isImporting}>{label('common.cancel')}</Button>
          <Button
            variant="primary"
            onClick={handleImport}
            disabled={!rows.length || !!parseError || isImporting || isChecking}
            className="max-sm:tw-flex-1"
          >
            {isImporting
              ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
              : <LuUpload aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {importLabel}
          </Button>
        </>
      )}
    >
      <ImportPanel
        kind="income"
        file={file}
        rows={rows}
        parseError={parseError}
        isDragging={isDragging}
        isChecking={isChecking}
        isImporting={isImporting}
        progress={progress}
        duplicateInfo={duplicateInfo}
        totalAmount={totalAmount}
        inputRef={fileInputRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onInputChange={handleFileInputChange}
        onRemoveFile={resetState}
      />
    </Modal>
  );
};

ImportIncomeExcelModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onImported: PropTypes.func,
};

export default ImportIncomeExcelModal;
